// Pure builder for the harness memory-store GRAPH — zero dependencies, so node
// tests and React components share it. Turns the implicit link structure of a
// memory store into an explicit node/edge graph the UI can draw and traverse.
//
// Two edge sources:
//   • the index (MEMORY.md) — `- [Title](slug.md) — hook` lines: MEMORY → leaf.
//   • leaf bodies — `[[slug]]` wikilinks (and any `[..](slug.md)` pointers): the
//     leaf → leaf cross-references that make the store a graph, not just a star.
//
// Node state is resolved against `capturedSlugs` (the set the sidebar lists,
// sliced from PROMPT_RESOURCE_PATHS): a node is `captured` when a scanned prompt
// actually read/wrote it, otherwise `ghost` (the index names it but the
// dashboard never captured its content — the gap that made the index look
// broken). `inIndex` flags whether the index lists it, so a captured-but-
// unindexed file shows up as an orphan rather than vanishing.
import { extractMemoryRefs, memorySlugFromHref } from "./memoryLinks.js";

const INDEX_SLUG = "MEMORY";

// Parse the index body's pointer lines into { slug, title, hook }. The index
// format is `- [Title](slug.md) — hook` (also tolerates ordered `1.`, a `>`
// quote prefix, and `-`/`:` hook separators). Lines without a memory pointer are
// skipped. First occurrence of a slug wins (the index shouldn't dup, but be
// safe).
export function parseIndexEntries(body) {
  const out = [];
  const seen = new Set();
  for (const raw of String(body ?? "").split("\n")) {
    const line = raw.trim();
    if (!line) continue;
    // Leading list/quote markers, then `[Title](href)`, then optional hook.
    const m = /^(?:[>\s]*)(?:[-*+]|\d+\.)?\s*\[([^\]]+)\]\(([^)]+)\)\s*(?:[—:–-]\s*(.*))?$/.exec(line);
    if (!m) continue;
    const slug = memorySlugFromHref(m[2]);
    if (!slug || seen.has(slug)) continue;
    seen.add(slug);
    out.push({ slug, title: m[1].trim(), hook: (m[3] || "").trim() });
  }
  return out;
}

// Build the store graph. All inputs optional so it degrades gracefully:
//   • index entries alone → an index→leaf star with captured/ghost nodes;
//   • + bodies → leaf↔leaf wikilink edges and meaningful backlinks.
// Returns { nodes, edges, backlinks }:
//   nodes:     [{ slug, title, hook, captured, inIndex, isIndex }] — index first,
//              then captured, then ghost; alpha within each band.
//   edges:     [{ from, to, kind: "index" | "wiki" }] — deduped.
//   backlinks: Map<slug, [{ from, kind }]> — reverse adjacency (who points here).
export function buildMemoryGraph({ indexEntries = [], capturedSlugs = new Set(), bodies = new Map(), current = null, includeCapturedOrphans = false } = {}) {
  const captured = capturedSlugs instanceof Set ? capturedSlugs : new Set(capturedSlugs || []);
  const nodes = new Map();
  const titleOf = new Map();
  const hookOf = new Map();
  for (const e of indexEntries) {
    if (e?.title) titleOf.set(e.slug, e.title);
    if (e?.hook) hookOf.set(e.slug, e.hook);
  }

  const ensure = (slug, { inIndex = false } = {}) => {
    if (!slug) return null;
    let n = nodes.get(slug);
    if (!n) {
      n = {
        slug,
        title: slug === INDEX_SLUG ? "Memory index" : (titleOf.get(slug) || slug),
        hook: hookOf.get(slug) || "",
        captured: captured.has(slug),
        inIndex: false,
        isIndex: slug === INDEX_SLUG,
      };
      nodes.set(slug, n);
    }
    if (inIndex) n.inIndex = true;
    return n;
  };

  // The index node always exists as the root.
  ensure(INDEX_SLUG);

  const edges = [];
  const edgeSeen = new Set();
  const addEdge = (from, to, kind) => {
    if (!from || !to || from === to) return;
    const k = `${from}->${to}`;
    if (edgeSeen.has(k)) return;
    edgeSeen.add(k);
    edges.push({ from, to, kind });
  };

  // Index → leaf edges.
  for (const e of indexEntries) {
    ensure(e.slug, { inIndex: true });
    addEdge(INDEX_SLUG, e.slug, "index");
  }

  // Captured-but-unindexed files become orphan nodes only when asked — the
  // per-version index map keeps its node universe to what THAT index points at
  // (so the map changes as you scrub versions), and surfaces orphans as a
  // separate count instead.
  if (includeCapturedOrphans) for (const slug of captured) ensure(slug);

  // Leaf → leaf wikilink edges from hydrated bodies — drawn only BETWEEN nodes
  // that already exist, so a stray wikilink can't balloon the graph past what
  // this index references. The index's OWN hydrated body is skipped on purpose:
  // index→leaf edges come from `indexEntries` (the authoritative, version-
  // correct source). Folding in whatever latest index happened to be hydrated is
  // exactly what made the map look identical across versions.
  const bmap = bodies instanceof Map ? bodies : new Map();
  for (const [from, body] of bmap) {
    if (from === INDEX_SLUG || !nodes.has(from)) continue;
    for (const to of extractMemoryRefs(body)) {
      if (!nodes.has(to)) continue;
      addEdge(from, to, "wiki");
    }
  }

  // Reverse adjacency.
  const backlinks = new Map();
  for (const { from, to, kind } of edges) {
    if (!backlinks.has(to)) backlinks.set(to, []);
    backlinks.get(to).push({ from, kind });
  }

  if (current) ensure(current);

  const band = (n) => (n.isIndex ? 0 : n.captured ? 1 : 2);
  const ordered = [...nodes.values()].sort(
    (a, b) => band(a) - band(b) || a.title.localeCompare(b.title),
  );

  return { nodes: ordered, edges, backlinks };
}
